import {
  TextField,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState, useCallback } from "react";
import { useAuth } from "../../AuthProvider";
import { useNavigate, Link } from "react-router-dom";
import GuestLayout from "../../Layouts/GuestLayout";
import SecondaryButton from "../../Components/SecondaryButton";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError("");
    setPasswordError("");
    setSnackbarMessage("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let hasError = false;

    if (!email) {
      setEmailError("The email field is empty.");
      hasError = true;
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email.");
      hasError = true;
    }

    if (!password) {
      setPasswordError("The password field is empty.");
      hasError = true;
    }

    if (hasError) return;

    setIsLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        navigate("/dashboard");
      } else {
        setSnackbarMessage(result.message);
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage("Error connecting to the server.");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
    if (emailError) setEmailError("");
  }, [emailError]);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
    if (passwordError) setPasswordError("");
  }, [passwordError]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <GuestLayout>
      <div className="flex w-full justify-center mb-[3rem]">
        <div className="flex text-3xl tracking-tight text-amber-500 font-semibold">
          Sign up to
          <div className="text-white flex items-center ml-2">
            <img src="/img/cajuu.png" alt="Cajuu Logo" className="w-9 h-9" />
            Cajuu<span className="text-[#2f2f2f]/40">.</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <TextField
          label="Email"
          className="w-full"
          color="secondary"
          value={email}
          onChange={handleEmailChange}
          error={!!emailError}
          helperText={emailError}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: 'red !important',
            },
            '& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: 'currentColor',
            },
          }}
        />

        <div className="mt-4">
          <TextField
            label="Password"
            className="w-full"
            color="secondary"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={handlePasswordChange}
            error={!!passwordError}
            helperText={passwordError}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: 'red !important',
              },
              '& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: 'currentColor',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff className="text-white" />
                    ) : (
                      <Visibility className="text-white" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className="flex justify-between mt-4">
          <Link
            to="/forgot-password"
            className="underline underline-offset-4 hover:decoration-4 text-sm font-medium text-white hover:text-amber-500 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Forgot your password?
          </Link>
        </div>

        <div className="mt-10">
          <SecondaryButton
            type="submit"
            disabled={isLoading}
            className={`bg-amber-500 hover:bg-amber-600 hover:text-white border-none h-[3rem] text-slate-900 w-full justify-center transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 ${isLoading ? "opacity-70 cursor-not-allowed" : ""
              }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Loading...
              </div>
            ) : (
              "Submit"
            )}
          </SecondaryButton>

          <Link
            to="/register"
            className="underline w-max block mx-auto mt-4 underline-offset-4 hover:decoration-4 text-sm font-medium text-white hover:text-amber-500 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Create an account
          </Link>
        </div>
      </form>

      {/* Snackbar para exibir erros */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{
            width: "500px",
            backgroundColor: "#dc2626", // vermelho forte
            color: "#fff", // texto branco
            "& .MuiAlert-icon": {
              color: "#fff", // ícone branco
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </GuestLayout>
  );
}